import { FunctionComponent } from "react";

import { faBolt, faBriefcase, faGlobe, faNewspaper } from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { Icon } from "@/components";
import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { classNames } from "@/utils/classNames";

import { formatRelativeTime, SignalIconKind, SignalView } from "./recentSignalsBox.utils";

/** Per-kind tile icon + tint (mirrors the mockup's `.lp-signal-icon.{type}` colors). */
const SIGNAL_TILE: Record<SignalIconKind, { icon: IconDefinition; className: string }> = {
	linkedin: {
		icon: faLinkedinIn as IconDefinition,
		className: "bg-blue-action-200 text-brand-linkedin",
	},
	news: { icon: faNewspaper, className: "bg-blue-action-200 text-blue-action" },
	web: { icon: faGlobe, className: "bg-purple-100 text-purple-800" },
	hire: { icon: faBriefcase, className: "bg-background-warning text-text-warning" },
	generic: { icon: faBolt, className: "bg-blue-action-200 text-brand-light" },
};

/** One compact signal row: 28px icon tile + title + 1-line snippet + relative timestamp. */
export const RecentSignalRow: FunctionComponent<{ view: SignalView }> = ({ view }) => {
	const tile = SIGNAL_TILE[view.iconKind];
	const time = formatRelativeTime(view.timeIso);

	return (
		<div className="flex gap-2.5 p-3" data-testid="lead-panel-recent-signal">
			<span
				className={classNames(
					"flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
					tile.className
				)}
			>
				{view.iconKind === "linkedin" ? (
					<BrandsIcon className="h-3.5 w-3.5" icon={tile.icon} />
				) : (
					<Icon className="h-3.5 w-3.5" icon={tile.icon} />
				)}
			</span>

			<div className="min-w-0 flex-1">
				<p className="truncate text-[13px] font-semibold text-text">{view.title}</p>
				{view.snippet && <p className="line-clamp-1 text-xs text-text-secondary">{view.snippet}</p>}
				{time && <p className="mt-0.5 text-[10.5px] text-text-tertiary">{time}</p>}
			</div>
		</div>
	);
};
